import React, { useEffect, useState, useCallback } from 'react'
import { Typography, Stack, CardContent, Card, Box, CircularProgress } from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import stylesheet from '../detail-container.stylesheet'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import { PatientService } from '../../../../services/patient.service'
import { PatientPharmacy, formatPhone } from '../../../../model/provider.types'
import { PharmacyManager } from '../../../pharmacy'
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy'
import StarIcon from '@mui/icons-material/Star'
import EmptyCardComponent from '../common/empty-card.component'

interface IProps {
  classes: ClassNameMap
  editPharmacyOpen?: boolean
  onClosePharmacyEdit?: () => void
}

const patientService = new PatientService()

const PatientPharmacyComponent: React.FC<IProps> = ({ classes, editPharmacyOpen = false, onClosePharmacyEdit }) => {
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const [pharmacies, setPharmacies] = useState<PatientPharmacy[]>([])
  const [loading, setLoading] = useState(false)

  // Load pharmacies
  useEffect(() => {
    if (patientDetail?.id)
      setPharmacies(patientDetail?.pharmacies || [])
  }, [patientDetail])

  const updatePharmacies = () => {
    if (patientDetail?.id) {
      setLoading(true)
      patientService.getPatientPharmacies(patientDetail.id)
        .then((res: any) => {
          setPharmacies(res?.data?.pharmacies || [])
        })
        .catch((err: any) => {
          console.error('Failed to load pharmacies:', err)
          setPharmacies([])
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  // Render a single pharmacy
  const renderPharmacy = (patientPharmacy: PatientPharmacy) => {
    const pharmacy = patientPharmacy.pharmacy
    if (!pharmacy) return null

    return (
      <Box key={patientPharmacy.id} mb={1} className={classes.customMiniCard}>
        <Card variant="outlined" className={classes.compactCard}>
          <CardContent className={classes.cardsContentPadding}>
            <Stack direction="column" spacing={0.5} className={classes.compactCardContent}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <LocalPharmacyIcon sx={{ fontSize: 14 }} color="action" />
                  <Typography variant="body2" className={classes.desc} sx={{ fontWeight: 500 }}>
                    {patientPharmacy.is_preferred && (
                      <StarIcon sx={{ fontSize: 14, color: 'gold', verticalAlign: 'middle', mr: 0.5 }} />
                    )}
                    {pharmacy.store_name}
                  </Typography>
                </Stack>
                {patientPharmacy.is_preferred && (
                  <Typography variant="caption" sx={{
                    backgroundColor: 'var(--color-badge-info-bg)',
                    color: 'var(--color-badge-info-text)',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    fontSize: '0.65rem'
                  }}>
                    Preferred
                  </Typography>
                )}
              </Stack>

              {/* Address */}
              <Typography variant="caption" color="textSecondary" className={classes.desc} sx={{ pr: 1 }}>
                {[pharmacy.address_1, pharmacy.city, pharmacy.state, pharmacy.postal_code]
                  .filter(Boolean).join(', ')}
              </Typography>

              {/* Phone and Fax - fax is bold per original requirements */}
              {(pharmacy.phone || pharmacy.fax) && (
                <Typography variant="caption" color="textSecondary" className={classes.desc} sx={{ pr: 1 }}>
                  {pharmacy.phone && `Ph: ${formatPhone(pharmacy.phone)}`}
                  {pharmacy.phone && pharmacy.fax && ' | '}
                  {pharmacy.fax && <strong>Fax: {formatPhone(pharmacy.fax)}</strong>}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Stack direction="column" className={classes.evalToxCard}>
      {loading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={20} />
        </Box>
      ) : pharmacies.length === 0 ? (
        <EmptyCardComponent msg={'No pharmacies assigned'} align='left' />
      ) : (
        <Stack direction="column" spacing={0}>
          {pharmacies.map((pp: PatientPharmacy) => renderPharmacy(pp))}
        </Stack>
      )}

      {/* PharmacyManager Dialog */}
      {patientDetail?.id && (
        <PharmacyManager
          patientId={patientDetail.id}
          open={editPharmacyOpen}
          onClose={() => onClosePharmacyEdit?.()}
          onUpdate={updatePharmacies}
        />
      )}
    </Stack>
  )
}

export default withStyles(stylesheet)(PatientPharmacyComponent)
